import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import { Document, Packer, Paragraph, HeadingLevel, Table, TableRow, TableCell, WidthType, TextRun } from "docx";
import { saveAs } from "file-saver";
import { formConfig, SectionConfig } from "../formConfig";

export interface FlatRow {
  section: string;
  question: string;
  value: string;
}

function formatValue(v: unknown): string {
  if (v == null || v === "") return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) {
    return v.map((item) => formatValue(item)).filter(Boolean).join(", ");
  }
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    if (typeof o.displayName === "string") return o.displayName;
    if (typeof o.name === "string" && typeof o.url === "string") return o.name;
    if (typeof o.name === "string") return o.name;
    // likert row->col map
    const entries = Object.entries(o).filter(([, val]) => typeof val === "string" && val);
    if (entries.length) return entries.map(([k, val]) => `${k}: ${val}`).join("; ");
    return "";
  }
  return String(v);
}

export function flattenSubmission(
  data: Record<string, unknown>,
  sections: SectionConfig[] = formConfig
): FlatRow[] {
  const rows: FlatRow[] = [];
  const seen = new Set<string>();
  for (const sec of sections) {
    for (const f of sec.fields) {
      seen.add(f.id);
      rows.push({ section: sec.title, question: f.question, value: formatValue(data[f.id]) });
    }
  }
  // Include any extra keys not covered by the form config.
  const extras = Object.keys(data).filter((k) => !seen.has(k));
  if (extras.length) {
    for (const k of extras) {
      rows.push({ section: "Other", question: k, value: formatValue(data[k]) });
    }
  }
  return rows;
}

function baseName(data: Record<string, unknown>): string {
  const name = (data.projectName as string) || "submission";
  const job = data.jobNumber ? `-${data.jobNumber}` : "";
  return `${name}${job}`.replace(/[^\w.-]+/g, "_").slice(0, 80);
}

function heading(data: Record<string, unknown>): string {
  const name = (data.projectName as string) || "Project Setup Submission";
  const job = data.jobNumber ? ` (#${data.jobNumber})` : "";
  return `${name}${job}`;
}

function isFileValue(v: unknown): v is { name: string; url: string } {
  return (
    !!v &&
    typeof v === "object" &&
    typeof (v as Record<string, unknown>).name === "string" &&
    typeof (v as Record<string, unknown>).url === "string"
  );
}

// The stored file `url` is a bare blob URL; the storage account correctly disallows
// anonymous public access, so it must always be opened through the authenticated
// download proxy rather than shared/opened directly.
function toDownloadUrl(url: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/api/download?url=${encodeURIComponent(url)}`;
}

function rewriteFileUrls(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(rewriteFileUrls);
  if (isFileValue(value)) return { ...value, url: toDownloadUrl(value.url) };
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) out[k] = rewriteFileUrls(v);
    return out;
  }
  return value;
}

export function exportJson(data: Record<string, unknown>): void {
  const safeData = rewriteFileUrls(data) as Record<string, unknown>;
  const blob = new Blob([JSON.stringify(safeData, null, 2)], { type: "application/json;charset=utf-8" });
  saveAs(blob, `${baseName(data)}.json`);
}

function buildHtml(data: Record<string, unknown>): string {
  const rows = flattenSubmission(data);
  const grouped = new Map<string, FlatRow[]>();
  for (const r of rows) {
    if (!grouped.has(r.section)) grouped.set(r.section, []);
    grouped.get(r.section)!.push(r);
  }
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  let body = `<h1>${esc(heading(data))}</h1>`;
  for (const [section, items] of grouped) {
    body += `<h2>${esc(section)}</h2><table><tbody>`;
    for (const it of items) {
      body += `<tr><th>${esc(it.question)}</th><td>${esc(it.value).replace(/\n/g, "<br>")}</td></tr>`;
    }
    body += `</tbody></table>`;
  }
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(heading(data))}</title>
<style>
  body{font-family:Segoe UI,Arial,sans-serif;color:#0f172a;margin:32px;}
  h1{margin:0 0 16px;} h2{margin:22px 0 6px;color:#1e293b;border-bottom:2px solid #e2e8f0;padding-bottom:4px;}
  table{border-collapse:collapse;width:100%;font-size:14px;margin-bottom:8px;}
  th{text-align:left;width:40%;vertical-align:top;padding:6px 10px;border:1px solid #e2e8f0;background:#f8fafc;font-weight:600;}
  td{padding:6px 10px;border:1px solid #e2e8f0;}
</style></head><body>${body}</body></html>`;
}

export function exportHtml(data: Record<string, unknown>): void {
  const blob = new Blob([buildHtml(data)], { type: "text/html;charset=utf-8" });
  saveAs(blob, `${baseName(data)}.html`);
}

export function printSubmission(data: Record<string, unknown>): void {
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(buildHtml(data));
  w.document.close();
  w.focus();
  setTimeout(() => {
    w.print();
  }, 300);
}

export function exportExcel(data: Record<string, unknown>): void {
  const rows = flattenSubmission(data);
  const aoa = [["Section", "Question", "Answer"], ...rows.map((r) => [r.section, r.question, r.value])];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = [{ wch: 22 }, { wch: 48 }, { wch: 60 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Submission");
  XLSX.writeFile(wb, `${baseName(data)}.xlsx`);
}

export function exportPdf(data: Record<string, unknown>): void {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const marginX = 40;
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  const maxWidth = pageWidth - marginX * 2;
  let y = 50;

  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - 40) {
      doc.addPage();
      y = 50;
    }
  };

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(heading(data), marginX, y);
  y += 24;

  const rows = flattenSubmission(data);
  let currentSection = "";
  for (const r of rows) {
    if (r.section !== currentSection) {
      currentSection = r.section;
      ensureSpace(30);
      y += 8;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(30, 41, 59);
      doc.text(currentSection, marginX, y);
      y += 16;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(51, 65, 85);
    const qLines = doc.splitTextToSize(r.question, maxWidth) as string[];
    ensureSpace(qLines.length * 13 + 4);
    doc.text(qLines, marginX, y);
    y += qLines.length * 13;

    doc.setFont("helvetica", "normal");
    doc.setTextColor(15, 23, 42);
    const aLines = doc.splitTextToSize(r.value || "—", maxWidth) as string[];
    ensureSpace(aLines.length * 13 + 6);
    doc.text(aLines, marginX, y);
    y += aLines.length * 13 + 6;
  }

  doc.save(`${baseName(data)}.pdf`);
}

export async function exportWord(data: Record<string, unknown>): Promise<void> {
  const rows = flattenSubmission(data);
  const grouped = new Map<string, FlatRow[]>();
  for (const r of rows) {
    if (!grouped.has(r.section)) grouped.set(r.section, []);
    grouped.get(r.section)!.push(r);
  }

  const children: (Paragraph | Table)[] = [
    new Paragraph({ text: heading(data), heading: HeadingLevel.HEADING_1 })
  ];

  for (const [section, items] of grouped) {
    children.push(new Paragraph({ text: section, heading: HeadingLevel.HEADING_2 }));
    children.push(
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: items.map(
          (it) =>
            new TableRow({
              children: [
                new TableCell({
                  width: { size: 40, type: WidthType.PERCENTAGE },
                  children: [new Paragraph({ children: [new TextRun({ text: it.question, bold: true })] })]
                }),
                new TableCell({
                  width: { size: 60, type: WidthType.PERCENTAGE },
                  children: [new Paragraph(it.value || "—")]
                })
              ]
            })
        )
      })
    );
    children.push(new Paragraph(""));
  }

  const doc = new Document({ sections: [{ children }] });
  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${baseName(data)}.docx`);
}
